import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import {
  SHIFT_ALLOCATION_ID_PREFIX,
  SHIFT_ALLOCATION_ID_SUFFIX,
  DROPPABLE_ID_SEPARATOR,
  TEXTS
} from '../../utils/constants';
import Button from '@amzn/meridian/button';
import React, { useEffect, useMemo, useState, useContext, useCallback } from 'react';
import closeLargeTokens from '@amzn/meridian-tokens/base/icon/close-small';
import Icon from '@amzn/meridian/icon';
import Box from '@amzn/meridian/box';
import Select, { SelectOption } from '@amzn/meridian/select';
import SearchField from '@amzn/meridian/search-field';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import Badge from '@amzn/meridian/badge';
import { AVAILABLE_ASSOCIATES_VIEW_CONSTANTS } from '../ShiftAllocationConstants';
import { EmployeeIcon } from './EmployeeIcon';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { ShiftAllocationContext } from '../context/ShiftAllocationContext';
import {
  getAssociateInfoFromProcessAssignments,
  getEmployeeShiftStartMap,
  getUnavailableEmployeeIds,
  groupAssociatesByShiftStart
} from './utils';
import { EMPTY_PROCESS, PROCESS_PATHS } from '../data';

const AvailableAssociates = ({ setOpen }) => {
  //get all process paths here
  const processPaths = Object.values(PROCESS_PATHS)
    .filter(
      (path) => path !== PROCESS_PATHS.BENCH && path !== PROCESS_PATHS.FIVE_S && path !== PROCESS_PATHS.YARD_MARSHALL
    )
    .sort();
  const [selectedDisallowedPath, setSelectedDisallowedPath] = useState([]);
  const [selectedTrainedPath, setSelectedTrainedPath] = useState([]);
  const [selectedSuggestedPath, setSelectedSuggestedPath] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [clockedInCount, setClockedInCount] = useState(0);
  const [clockedOutCount, setClockedOutCount] = useState(0);
  const [currentTab, setCurrentTab] = useState('In');

  const { shiftAllocationModel, shiftAllocationDispatch, cachedResponse } = useContext(ShiftAllocationContext);

  // Map of employeeId -> shift start time, used to group bench associates under start-time headers.
  // Scope the starts to the currently selected board's time window (boardAllocationKey is
  // "cycle.phase.start.end") so an associate with another schedule later in the day does not
  // show up under a bogus start-time header that doesn't belong to this board.
  const boardAllocationKey = shiftAllocationModel && shiftAllocationModel.boardAllocationKey;
  const shiftStartMap = useMemo(() => {
    let boardStart;
    let boardEnd;
    if (boardAllocationKey) {
      const parts = boardAllocationKey.split('.');
      boardStart = parts[2];
      boardEnd = parts[3];
    }
    return getEmployeeShiftStartMap(cachedResponse, boardStart, boardEnd);
  }, [cachedResponse, boardAllocationKey]);

  // Associates whose accepted time off leaves them no availability inside this board window. They
  // are not placeable here, so they are kept off the bench rather than shown under a time header
  // outside the window or lumped in with "Unscheduled".
  const unavailableEmployeeIds = useMemo(() => {
    let boardStart;
    let boardEnd;
    if (boardAllocationKey) {
      const parts = boardAllocationKey.split('.');
      boardStart = parts[2];
      boardEnd = parts[3];
    }
    return getUnavailableEmployeeIds(cachedResponse, boardStart, boardEnd);
  }, [cachedResponse, boardAllocationKey]);

  const assignments = useMemo(() => {
    if (shiftAllocationModel.processInfo.find((process) => process.id === PROCESS_PATHS.BENCH)) {
      return shiftAllocationModel.processInfo.find((process) => process.id === PROCESS_PATHS.BENCH).assignments;
    } else {
      return [];
    }
  }, [shiftAllocationModel]);

  const visibleData = useMemo(() => {
    return Object.values(
      getAssociateInfoFromProcessAssignments(assignments, shiftAllocationModel['associateInfo'])
    ).filter((associate) => {
      return (
        !unavailableEmployeeIds.has(String(associate.associateId || associate.alias)) &&
        ((currentTab.includes('In') && associate.clockedInStatus) ||
          (currentTab.includes('Out') && !associate.clockedInStatus)) &&
        (selectedDisallowedPath.length > 0
          ? associate.disallowedPaths.some((path) => selectedDisallowedPath.includes(path))
          : true) &&
        (selectedTrainedPath.length > 0
          ? associate.trainedPaths.some((path) => selectedTrainedPath.includes(path))
          : true) &&
        (selectedSuggestedPath.length > 0
          ? associate.recommendedPaths.some((path) => selectedSuggestedPath.includes(path))
          : true) &&
        (searchQuery.length > 0
          ? (associate.name && associate.name.toLocaleLowerCase().includes(searchQuery.toLocaleLowerCase())) ||
            associate.alias.toLocaleLowerCase().includes(searchQuery.toLocaleLowerCase())
          : true)
      );
    });
  }, [
    selectedTrainedPath,
    selectedSuggestedPath,
    selectedDisallowedPath,
    searchQuery,
    currentTab,
    assignments,
    shiftAllocationModel['associateInfo'],
    unavailableEmployeeIds
  ]);

  useEffect(() => {
    const clockedIn = [];
    const clockedOut = [];

    // Deliberately NOT filtered by unavailableEmployeeIds. These badges report how many associates
    // are punched in or out across the shift, not how many are placeable on the board window
    // currently selected, so they can legitimately exceed the number of associates rendered below.
    Object.values(getAssociateInfoFromProcessAssignments(assignments, shiftAllocationModel['associateInfo'])).forEach(
      (associate) => {
        if (associate.clockedInStatus) {
          clockedIn.push(associate);
        } else {
          clockedOut.push(associate);
        }
      }
    );

    setClockedInCount(clockedIn.length);
    setClockedOutCount(clockedOut.length);
  }, [shiftAllocationModel['associateInfo'], assignments]);

  const onClickCloseButton = () => {
    setOpen(false);
  };

  const onClickClearButton = () => {
    // clear selection of all three filters
    setSelectedDisallowedPath([]);
    setSelectedTrainedPath([]);
    setSelectedSuggestedPath([]);
  };

  const onSubmit = (query) => {
    setSearchQuery(query);
  };

  const TabView = useCallback(() => {
    const groups = groupAssociatesByShiftStart(visibleData, shiftStartMap);

    // When the bench (for this tab/filters) is empty, still render one empty bench Droppable
    // so associates can be dragged back from the board onto the bench.
    if (groups.length === 0) {
      return (
        <Droppable
          droppableId={`${PROCESS_PATHS.BENCH}${DROPPABLE_ID_SEPARATOR}${EMPTY_PROCESS}${DROPPABLE_ID_SEPARATOR}${SHIFT_ALLOCATION_ID_SUFFIX.BENCH_DETAIL_CARD}`}
          type={'PROCESS_PATH'}
          direction="horizontal">
          {(dropProvided) => (
            <div ref={dropProvided.innerRef} {...dropProvided.droppableProps} style={{ minHeight: 40 }}>
              <Row alignmentVertical="top" alignmentHorizontal="start" spacing="200" wrap="down">
                {dropProvided.placeholder}
              </Row>
            </div>
          )}
        </Droppable>
      );
    }

    return (
      <Box>
        {groups.map((group) => {
          const groupKey = group.startIso || 'unscheduled';
          // Each shift-start group is its own Droppable so react-beautiful-dnd sees a
          // contiguous list of sibling Draggables (indices 0..n) per group. The id still
          // starts with the BENCH prefix so drop detection (split(DROPPABLE_ID_SEPARATOR)[0] === 'Bench') works.
          const groupDroppableId = `${PROCESS_PATHS.BENCH}${DROPPABLE_ID_SEPARATOR}${EMPTY_PROCESS}${DROPPABLE_ID_SEPARATOR}${SHIFT_ALLOCATION_ID_SUFFIX.BENCH_DETAIL_CARD}${DROPPABLE_ID_SEPARATOR}${groupKey}`;
          return (
            <Box key={groupKey} spacingInset="100 none none none">
              <Row alignmentVertical="center" spacing="100" spacingInset="100 none">
                <Text type={TEXTS.T3} color="secondary">
                  {group.label}
                </Text>
                <Badge value={group.associates.length} type="theme" />
              </Row>
              <Droppable
                droppableId={groupDroppableId}
                type={'PROCESS_PATH'}
                direction="horizontal"
                renderClone={(provided, snapshot, rubric) => (
                  <div {...provided.draggableProps} {...provided.dragHandleProps} ref={provided.innerRef}>
                    <EmployeeIcon
                      associate={shiftAllocationModel['associateInfo'][rubric.draggableId.split('#@#')[0]]}
                      isBenched
                    />
                  </div>
                )}>
                {(dropProvided) => (
                  <div ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                    <Row alignmentVertical="top" alignmentHorizontal="start" spacing="200" wrap="down">
                      {group.associates.map((associate, index) => {
                        const draggableId = group.startIso ? `${associate.alias}#@#${group.startIso}` : associate.alias;
                        return (
                          <Draggable
                            // adding a key is important! Suffix keeps it unique when an associate
                            // appears under more than one shift-start header.
                            key={draggableId}
                            draggableId={draggableId}
                            index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                id={`${SHIFT_ALLOCATION_ID_PREFIX.DRAGGABLE_ASSOCIATE}-${associate.alias}`}
                                style={{
                                  ...provided.draggableProps.style,
                                  position: snapshot.isDragging ? 'absolute' : 'static'
                                }}>
                                <EmployeeIcon associate={associate} isBenched />
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {dropProvided.placeholder}
                    </Row>
                  </div>
                )}
              </Droppable>
            </Box>
          );
        })}
      </Box>
    );
  }, [visibleData, shiftStartMap, shiftAllocationModel]);

  return (
    <Column spacingInset="300" type={'outline'} height={'100%'} width={'100%'} minWidth={'250px'} maxWidth={'400px'}>
      <Row alignmentHorizontal="justify" spacing="none">
        <Text type={TEXTS.H4}>{AVAILABLE_ASSOCIATES_VIEW_CONSTANTS.PAGE_HEADER}</Text>
        <Button type="link" onClick={onClickCloseButton} size="medium">
          <Icon tokens={closeLargeTokens}></Icon>
        </Button>
      </Row>
      <Row spacing="100">
        <Box type="fill" spacingInset="200" width="100%">
          <Row spacingInset="200" alignmentHorizontal="center">
            <Select
              value={selectedDisallowedPath}
              minWidth={'95%'}
              onChange={setSelectedDisallowedPath}
              label={AVAILABLE_ASSOCIATES_VIEW_CONSTANTS.DISALLOWED_PATH}
              size="xlarge">
              {processPaths.map((disallowedPath) => (
                <SelectOption key={disallowedPath} value={disallowedPath} label={disallowedPath} />
              ))}
            </Select>
          </Row>
          <Row spacingInset="200" alignmentHorizontal="center">
            <Select
              value={selectedTrainedPath}
              minWidth={'95%'}
              onChange={setSelectedTrainedPath}
              label={AVAILABLE_ASSOCIATES_VIEW_CONSTANTS.TRAINED_PATH}
              size="xlarge">
              {processPaths.map((trainedPath) => (
                <SelectOption key={trainedPath} value={trainedPath} label={trainedPath} />
              ))}
            </Select>
          </Row>
          <Row spacingInset="200" alignmentHorizontal="center">
            <Select
              value={selectedSuggestedPath}
              minWidth={'95%'}
              onChange={setSelectedSuggestedPath}
              label={AVAILABLE_ASSOCIATES_VIEW_CONSTANTS.SUGGESTED_PATH}
              size="xlarge">
              {processPaths.map((suggestedPath) => (
                <SelectOption key={suggestedPath} value={suggestedPath} label={suggestedPath} />
              ))}
            </Select>
          </Row>
          <Button onClick={onClickClearButton} type="link">
            {AVAILABLE_ASSOCIATES_VIEW_CONSTANTS.CLEAR_ALL_BUTTON}
          </Button>
        </Box>
      </Row>
      <Box width="100%">
        <SearchField
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder={AVAILABLE_ASSOCIATES_VIEW_CONSTANTS.SEARCH_PLACEHOLDER}
          onSubmit={onSubmit}
          size="medium"
          searchButton={true}
          disabled={false}
          clearButton={true}
          minWidth={'95%'}
        />
      </Box>
      <Row spacing="none" alignmentHorizontal="justify" alignmentVertical={'top'} wrap={'down'}>
        <TabGroup fill={'tabs'} value={currentTab} onChange={setCurrentTab}>
          <Tab value="In">
            <Text>
              {AVAILABLE_ASSOCIATES_VIEW_CONSTANTS.TAB_IN}
              <Badge value={clockedInCount} type="theme" />
            </Text>
          </Tab>

          <Tab value="Out">
            <Text>
              {AVAILABLE_ASSOCIATES_VIEW_CONSTANTS.TAB_OUT}
              <Badge value={clockedOutCount} type="theme" />
            </Text>
          </Tab>
        </TabGroup>
      </Row>
      <div
        // some magic numbers but basically that's the width of one of the
        // employeeIcons + margins
        style={{ width: `${65 * 4}px` }}
        id={PROCESS_PATHS.BENCH}>
        {currentTab === 'In' && (
          <div role="tabpanel">
            <TabView />
          </div>
        )}
        {currentTab === 'Out' && (
          <div role="tabpanel">
            <TabView />
          </div>
        )}
      </div>
    </Column>
  );
};

export default AvailableAssociates;
